import { createSelector } from "reselect";
import _ from "lodash";
import dateUtil from "../common/date/dateUtil";
import * as types from "./dashboardActionTypes";
import {
  IncidentsMock,
  incidentsSummaryMock
} from "./widgets/incidentsMockData";
import * as commentTypes from '../incidents/comments/commentTypes';
const incidentsSelector = state => state.incidents;
const dashboardIncidentsSelector = state => state.incidents.incidents;
const dashboardIncidentsSummarySelector = state =>
  state.incidents.incidentsSummary;
const incidentsIsLoadingSelector = state => state.incidentsIsLoading;
const deviceGroupsSelector = state => state.deviceGroups;
const deviceGroupsIsLoadingSelector = state => state.deviceGroupsIsLoading;

const systemStatusSelector = state => state.systemStatus;
const systemStatusIsLoadingSelector = state => state.systemStatusIsLoading;
const errorsSelector = state => state.errors;
const securityAlertsLastMonthSelector = state => state.securityAlertsLastMonth;
const securityAlertsLastMonthIsLoadingSelector = state =>
  state.securityAlertsLastMonthIsLoading;
const securityAlertsLastWeekSelector = state => state.securityAlertsLastWeek;
const securityALertsLastWeekIsLoadingSelector = state =>
  state.securityAlertsLastWeekIsLoading;
const logsPerDaySelector = state => state.logsPerDay;
const logsPerDayIsLoadingSelector = state => state.logsPerDayIsLoading;

const severity = {
  low: 0,
  medium: 1,
  high: 2
};

const mapToSecurityAlertsLastMonthChartData = severityCounts => {
  const lowSeverityCount = severityCounts.lowSeverities;
  const mediumSeverityCount = severityCounts.mediumSeverities;
  const highSeverityCount = severityCounts.highSeverities;

  const severityLabels = {
    lowSeverity: { count: lowSeverityCount, severity: 0 },
    mediumSeverity: { count: mediumSeverityCount, severity: 1 },
    highSeverity: { count: highSeverityCount, severity: 2 }
  };

  const highSeverityColor = "#d419a8";
  const mediumSeverityColor = "#fd9e29";
  const lowSeverityColor = "#2cbafc";
  const data = [lowSeverityCount, mediumSeverityCount, highSeverityCount];
  const datasets = [
    {
      data: data,
      backgroundColor: [
        lowSeverityColor,
        mediumSeverityColor,
        highSeverityColor
      ]
    }
  ];

  return {
    labels: severityLabels,
    datasets: datasets,
    severityCounts: severityCounts
  };
};

const mapToSecurityAlertsLastWeekChartData = securityAlerts => {
  const highSeverityColor = "#d419a8";
  const mediumSeverityColor = "#fd9e29";
  const lowSeverityColor = "#2cbafc";

  const datasets = [
    {
      type: "bar",
      label: severity.low,
      data: [
        securityAlerts.firstDay.lowSeverityCount,
        securityAlerts.secondDay.lowSeverityCount,
        securityAlerts.thirdDay.lowSeverityCount,
        securityAlerts.fourthDay.lowSeverityCount,
        securityAlerts.fifthDay.lowSeverityCount,
        securityAlerts.sixthDay.lowSeverityCount,
        securityAlerts.seventhDay.lowSeverityCount
      ],
      backgroundColor: lowSeverityColor
    },
    {
      type: "bar",
      label: severity.medium,
      data: [
        securityAlerts.firstDay.mediumSeverityCount,
        securityAlerts.secondDay.mediumSeverityCount,
        securityAlerts.thirdDay.mediumSeverityCount,
        securityAlerts.fourthDay.mediumSeverityCount,
        securityAlerts.fifthDay.mediumSeverityCount,
        securityAlerts.sixthDay.mediumSeverityCount,
        securityAlerts.seventhDay.mediumSeverityCount
      ],
      backgroundColor: mediumSeverityColor
    },
    {
      type: "bar",
      label: severity.high,
      data: [
        securityAlerts.firstDay.highSeverityCount,
        securityAlerts.secondDay.highSeverityCount,
        securityAlerts.thirdDay.highSeverityCount,
        securityAlerts.fourthDay.highSeverityCount,
        securityAlerts.fifthDay.highSeverityCount,
        securityAlerts.sixthDay.highSeverityCount,
        securityAlerts.seventhDay.highSeverityCount
      ],
      backgroundColor: highSeverityColor
    }
  ];
  const labels = Object.keys(securityAlerts)
    .filter(k => securityAlerts[k].timestamp != null)
    .map(k => dateUtil.toDayAndMonth(securityAlerts[k].timestamp));

  return {
    labels: labels,
    datasets: datasets
  };
};

const mapToLogsPerDayChartData = logsPerDay => {
  const labels = logsPerDay.logSizeDay.map(l =>
    dateUtil.toDayAndMonth(l.timestamp)
  );
  const data = logsPerDay.logSizeDay.map(l => l.size);
  const bgs = logsPerDay.logSizeDay.map(l => {
    return l.size > logsPerDay.logSpaceThresholdPerDayGb
      ? "#EB0808"
      : "#8c1dd0";
  });

  return {
    labels: labels,
    isLogLimitReached: logsPerDay.isLogLimitReached,
    logSpaceThresholdPerDayGb: logsPerDay.logSpaceThresholdPerDayGb,
    datasets: [
      {
        data: data,
        backgroundColor: bgs
      }
    ]
  };
};

export const getDashboardIncidentsSummary = createSelector(
  dashboardIncidentsSummarySelector,
  incidentSummary =>
    incidentSummary != null ? incidentSummary : incidentsSummaryMock
);

export const getDashboardIncidents = createSelector(
  dashboardIncidentsSelector,
  incidents => {
    if (incidents != null) {
      return incidents.map(i => {
        const cropSize = 25;
        const shouldCrop = i.Subject.length > cropSize;
        const latestAction = i.LatestComment.CommentType;

        const Status = i.Status === 'Closed' ? 'Closed' : i.Status === 'Open' && (latestAction=== commentTypes.RESOLUTION_PROPOSAL 
          || latestAction === commentTypes.COMMENT_TO_CUSTOMER) ? 'New'  : (latestAction === commentTypes.RESOLUTION_PROPOSAL_CLOSED 
          || latestAction === commentTypes.RESOLUTION_PROPOSAL_IMPLEMENTED 
          || latestAction === commentTypes.RESOLUTION_PROPOSAL_REJECTED) ? 'Closed': 
           latestAction === commentTypes.COMMENT_FROM_CUSTOMER ? 'Waiting': latestAction === commentTypes.RESOLUTION_PROPOSAL_RECEIVED ? 'Open': i.Status;
        
        return {
          id: i.Id,
          priority: i.Priority,
          status: Status,
          date: dateUtil.toDayAndMonth(i.Date),
          subject: shouldCrop
            ? `${i.Subject.substring(0, cropSize)}...`
            : i.Subject
        };
      });
    } else {
      return IncidentsMock;
    }
  }
);
export const getIncidents = createSelector(
  incidentsSelector,
  incidents => incidents
);
export const getIncidentsIsLoading = createSelector(
  incidentsIsLoadingSelector,
  incidentsIsLoading => incidentsIsLoading
);
export const getIncidentsHasErrors = createSelector(
  errorsSelector,
  getDashboardIncidentsSummary,
  (errors, incidentsSummary) => {
    const incidentsError = errors.filter(
      err => err.key === types.LOAD_INCIDENTS_FAILED
    );

    const hasErrors = incidentsError.length > 0;

    return hasErrors;
  }
);

export const getDeviceGroups = createSelector(
  deviceGroupsSelector,
  deviceGroups => deviceGroups
);
export const getDeviceGroupsIsLoading = createSelector(
  deviceGroupsIsLoadingSelector,
  deviceGroupsIsLoading => deviceGroupsIsLoading
);
export const getSystemStatus = createSelector(
  systemStatusSelector,
  systemStatus => systemStatus
);
export const getSystemStatusIsLoading = createSelector(
  systemStatusIsLoadingSelector,
  systemStatusIsLoading => systemStatusIsLoading
);
export const getSecurityAlertsLastMonth = createSelector(
  securityAlertsLastMonthSelector,
  securityAlertsLastMonth =>
    mapToSecurityAlertsLastMonthChartData(securityAlertsLastMonth)
);
export const getSecurityAlertsLastMonthIsLoading = createSelector(
  securityAlertsLastMonthIsLoadingSelector,
  securityAlertsLastMonthIsLoading => securityAlertsLastMonthIsLoading
);
export const getSecurityAlertsLastWeek = createSelector(
  securityAlertsLastWeekSelector,
  securityAlertsLastWeek =>
    mapToSecurityAlertsLastWeekChartData(securityAlertsLastWeek)
);
export const getSecurityAlertsLastWeekIsLoading = createSelector(
  securityALertsLastWeekIsLoadingSelector,
  securityAlertsLastWeekIsLoading => securityAlertsLastWeekIsLoading
);
export const getLogsPerDay = createSelector(logsPerDaySelector, logsPerDay =>
  mapToLogsPerDayChartData(logsPerDay)
);
export const getLogsPerDayIsLoading = createSelector(
  logsPerDayIsLoadingSelector,
  logsPerDayIsLoading => logsPerDayIsLoading
);

export const getSystemStatusHasErrors = createSelector(
  errorsSelector,
  errors => {
    const systemStatusError = errors.filter(
      err => err.key === types.LOAD_SYSTEM_STATUS_FAILED
    );
    const hasErrors = systemStatusError.length > 0;
    return hasErrors;
  }
);

export const getSecurityAlertsLastWeekHasErrors = createSelector(
  errorsSelector,
  errors => {
    const lastWeekErrors = errors.filter(
      err => err.key === types.LOAD_SECURITY_ALERTS_FOR_LAST_WEEK_FAILED
    );
    const hasErrors = lastWeekErrors.length > 0;
    return hasErrors;
  }
);

export const getLogsPerDayHasErrors = createSelector(errorsSelector, errors => {
  const logsPerDayError = errors.filter(
    err => err.key === types.LOAD_LOGS_PER_DAY_FAILED
  );
  const hasErrors = logsPerDayError.length > 0;
  return hasErrors;
});



// WEBPACK FOOTER //
// ./src/dashboard/dashboardSelector.js